import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../config";
import { Check, Loader2, Sparkles } from "lucide-react";
import { PULSEAI_METADATA, PulseAIImage } from "../PulseAIImage";
import AdminChatbotToggle from "./AdminChatbotToggle";
import AdminSwitcherAnalytics from "./AdminSwitcherAnalytics";

const AdminPulseAITab = () => {
  const [active, setActive] = useState("f_hybrid");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pulseai/active`);
      setActive(res.data.key || "f_hybrid");
    } catch (e) {
      console.error("PulseAI load error:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSelect = async (key) => {
    setSaving(key);
    try {
      await axios.post(`${API}/pulseai/select`, { variant: key });
      setActive(key);
      try {
        localStorage.setItem("pulseai_active", key);
      } catch {}
    } catch (e) {
      console.error("PulseAI select error:", e);
      alert(e.response?.data?.detail || "Chyba pri výbere variantu");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div data-testid="admin-pulseai-panel" className="space-y-6">
      {/* V179 — Chatbot landing toggle */}
      <AdminChatbotToggle />

      {/* V185 — Switcher funnel analytics */}
      <AdminSwitcherAnalytics />

      {/* Header */}
      <div className="bg-gradient-to-r from-purple-900 via-indigo-900 to-blue-900 rounded-xl p-6 border border-purple-500/30">
        <div className="flex items-center gap-3 mb-2">
          <Sparkles className="w-6 h-6 text-cyan-300" />
          <h2 className="text-xl font-bold text-white">PulseAI Engine™ Brand</h2>
        </div>
        <p className="text-purple-200 text-sm">
          Vyber si dychberúcu vizuálnu identitu AI enginu. 9 hyperrealistických
          obrázkov generovaných cez GPT-image-1, cinematic quality. Zvolený
          variant sa zobrazí v Hero, Showcase sekcii a AI výstupoch.
        </p>
        {active && (
          <p className="text-xs text-cyan-300 mt-3 font-mono">
            AKTÍVNY: {active}
          </p>
        )}
      </div>

      {loading ? (
        <div className="p-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-400" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(PULSEAI_METADATA).map(([key, meta]) => {
            const isActive = active === key;
            return (
              <div
                key={key}
                data-testid={`pulseai-variant-${key}`}
                className={`relative bg-white rounded-xl border-2 overflow-hidden transition-all ${
                  isActive
                    ? "border-purple-500 ring-2 ring-purple-300 shadow-xl"
                    : "border-gray-200 hover:border-gray-300 hover:shadow-lg"
                }`}
              >
                {isActive && (
                  <div className="absolute top-3 right-3 z-10 bg-purple-600 text-white text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1 shadow-lg">
                    <Check className="w-3 h-3" />
                    AKTÍVNY
                  </div>
                )}

                {/* Image preview */}
                <div className="aspect-square bg-gradient-to-br from-gray-950 to-gray-800 relative overflow-hidden">
                  <PulseAIImage
                    variantKey={key}
                    size={520}
                    rounded={false}
                    className="absolute inset-0"
                  />
                </div>

                {/* Info */}
                <div className="p-4">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-bold text-gray-900 text-sm">
                      {meta.label}
                    </h3>
                    <span className="text-[10px] font-mono uppercase bg-gray-100 px-1.5 py-0.5 rounded text-gray-500">
                      {key}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mb-1 font-medium">
                    {meta.style}
                  </p>
                  <p className="text-xs text-gray-600 leading-relaxed mb-3">
                    {meta.desc}
                  </p>

                  <button
                    onClick={() => handleSelect(key)}
                    disabled={isActive || saving === key}
                    data-testid={`pulseai-select-${key}`}
                    className={`w-full px-3 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5 ${
                      isActive
                        ? "bg-purple-100 text-purple-700 cursor-default"
                        : "bg-gray-900 text-white hover:bg-purple-600"
                    }`}
                  >
                    {saving === key ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : isActive ? (
                      <>
                        <Check className="w-3.5 h-3.5" /> Vybrané
                      </>
                    ) : (
                      "Vybrať tento variant"
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
        <p className="font-semibold mb-1">💡 O vizualizáciách</p>
        <p className="text-xs leading-relaxed">
          Každý variant je <strong>hyperrealistický obrázok</strong> generovaný cez
          OpenAI gpt-image-1 v 1024×1024 rozlíšení. Zvolený variant sa automaticky zobrazí:
        </p>
        <ul className="text-xs mt-2 space-y-1 list-disc list-inside">
          <li>v Hero sekcii landing page (Powered by PulseAI Engine™ badge)</li>
          <li>v Showcase sekcii "Meet PulseAI Engine™" (veľký cinematic vizuál)</li>
          <li>v loading stavoch AI funkcií (Brand Monitor, Strategy, Keyword Research)</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminPulseAITab;
